import { isFunction } from "lodash-es";
import React, { createContext, FunctionComponent, ReactNode, useCallback, useLayoutEffect, useState } from "react";

import { IAnimationContext } from "./types";

interface Props {
  animate: boolean;
  disabled?: boolean;
  children: (inAnimState: boolean, finish: () => void) => ReactNode | ReactNode[];
  onFinish?: () => void;
}

export const AnimationContext = createContext<IAnimationContext>({
  animating: false,
});

/**
 * This component sets up AnimationContext. Use with <AnimationTarget>'s as descendants.
 * When animate prop changes to true it will run animation of children.
 * When animate prop changes to false it will stop rendering children.
 * When onFinish is present then after changing animate prop to false, component will first
 * start finishing animation and when it ends then it will invoke onFinish handler
 * (e.g. first Openable fades out, then it gets unmounted from portal).
 *
 * @param {boolean} animate flag determining when to start or finish animation
 * @param {Function} onFinish when passed a function the component will ignore setting `animate` prop to false
 * and will only allow finishing animation by invoking `finish` function provided to children. After animation
 * ends then `onFinish` handler will be fired (where you can close/unmount for example a modal).
 * @param {Function} children function which renders children and provides them with 'inAnimState' and 'finish'
 * arguments. Flag `inAnimState` will mirror `animate` prop (note: not when `onFinished` prop is provided!).
 * Function `finish` allows children to request closing animation (then after animation is finished,
 * onFinish callback will be fired).
 * `finish` function only works when onFinish callback is provided.
 * @returns {FunctionComponent}
 */
export const Animation: FunctionComponent<Props> = ({ animate, children, onFinish }) => {
  const [inAnimState, setInAnimState] = useState<boolean>(!!animate);
  const [animating, setAnimating] = useState<boolean>(!!animate);

  useLayoutEffect(() => {
    if (onFinish) {
      if (animate) {
        setInAnimState(true);
        setAnimating(true);
      }
    } else {
      setInAnimState(!!animate);
      setAnimating(!!animate);
    }
  }, [animate, onFinish]);

  const context: IAnimationContext = {
    animating,
    onFinish: () => {
      if (isFunction(onFinish)) {
        onFinish();
      }
      setInAnimState(false);
    },
  };

  const finish = useCallback(() => {
    setAnimating(false);
  }, []);

  if (!children) {
    return null;
  }

  return <AnimationContext.Provider value={context}>{children(inAnimState, finish)}</AnimationContext.Provider>;
};
